import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { AuthProvider, useAuth } from "./context/AuthContext";
import reportWebVitals from "./reportWebVitals";

// Catch and log the exact bad value before React crashes
const originalError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === "string" &&
    args[0].includes("Objects are not valid as a React child")
  ) {
    console.error("REACT CHILD ERROR DETECTED!");
    console.error("The invalid object is:", args[1]);
    console.error("Full args:", args);
  }
  originalError.apply(console, args);
};

// Global Error Boundary that survives production builds
class GlobalErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("GlobalErrorBoundary caught an error:", error);
    console.error("Component stack:", info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="max-w-2xl p-8 bg-white rounded-lg shadow-lg">
          <h1 className="text-3xl font-bold text-red-600 mb-4">
            Application crashed
          </h1>
          <pre className="text-sm bg-gray-900 text-gray-100 p-4 rounded overflow-auto">
            {this.state.error?.toString()}
            {"\n\n"}
            {this.state.error?.stack}
          </pre>
        </div>
      </div>
      );
    }
    return this.props.children;
  }
}

function Root() {
  const { user, loading } = useAuth();

  // No login form: while loading, or while AuthContext is redirecting an
  // unauthenticated user to Keycloak, show a splash. Render the app only once
  // a session exists.
  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">
          {loading ? "Loading..." : "Redirecting to sign in..."}
        </div>
      </div>
    );
  }

  return <App />;
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <AuthProvider>
        <Root />
      </AuthProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();